import { Component, computed, effect, EventEmitter, inject, input, OnInit, output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FuseConfigService } from '../../../core/services/config.service';
import { UserService } from '../../../core/services/users.service';
import { PictureModalComponent } from '../../../main/pages/pictures-modal/picture-modal';
import { ToastrService } from 'ngx-toastr';
import ImageCompressor from 'image-compressor.js';
import { FuseDeleteDialogComponent } from '../delete-dialog/delete-dialog.component';
import { EventService } from '../../services/event.service';
import { TranslateService } from '@ngx-translate/core';
import imageCompression from 'browser-image-compression';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'cropper-component',
  templateUrl: './cropper.html',
  styleUrls: ['./cropper.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatCheckboxModule,
    ImageCropperComponent,
    TranslateModule
  ]
})
export class CropperComponent {
  // Services
  public dialog = inject(MatDialog);
  private userService = inject(UserService);
  private fuseConfig = inject(FuseConfigService);
  public toastr = inject(ToastrService);
  public eventService = inject(EventService);
  private translate = inject(TranslateService);

  // Inputs
  avatar = input<any>();
  page = input<string>();
  defaultRatio = input<any>();
  file = input<any>();

  // Outputs
  sendImg = output<any>();
  cropperOpened = output<boolean>();

  // Signals
  id = Date.now().toString(36) + Math.random().toString(36).substr(2);
  imageChangedEvent = signal<any>('');
  hideRatioPages = signal<string[]>(['event', 'blog-new', 'picture', 'elearning']);

  // Computed signals for page groups
  isProfilePage = computed(() => {
    const p = this.page();
    return ['fr-user', 'user', 'profile', 'user-detail', 'fr-user-detail'].includes(p);
  });

  isChatPage = computed(() => {
    const p = this.page();
    return ['chat_setting'].includes(p);
  });

  isOrgPage = computed(() => {
    const p = this.page();
    return ['org', 'org-detail'].includes(p);
  });

  showEventUploadTitle = computed(() => {
    const p = this.page();
    return !this.isProfilePage() && !this.isOrgPage() && !this.isChatPage() && p !== 'elearning';
  });

  uploadedImg = signal<any>(null);
  defaultImage = signal<any>(null);
  defaultImageFile = signal<any>(null);
  img = signal<string>('');
  imgBase64 = signal<string>('');
  imageFromCollection = signal<boolean>(false);
  imgEvent = signal<any>(null);
  openCropper = signal<boolean>(false);
  showAvatarImage = signal<boolean>(true);
  fileName = signal<string>('');
  cropptedFile = signal<any>(null);
  showUndo = signal<boolean>(false);
  useRatio = signal<boolean>(false);
  ratioX = signal<number>(1);
  ratioY = signal<number>(1);
  zoomScale = signal<number>(200);
  hideRatio = signal<boolean>(false);
  firstImageLoad = signal<boolean>(false);
  lastImage = signal<string>('');
  tempImage = signal<string>('');
  imageFromUser = signal<string>('');
  collectionImage = signal<string>('');
  showCropMessage = signal<boolean>(false);

  constructor() {
    effect(() => {
      const avatarVal = this.avatar();
      if (avatarVal) {
        if (this.page() === 'profile' || this.page() === 'user-detail' || this.page() === 'canton-detail' ||
          this.page() === 'org-detail' || this.page() === 'fr-user-detail' || this.page() === 'setting-form' ||
          this.page() === 'elearning' || this.page() === 'event' || this.page() === 'chat_setting') {
          this.img.set(avatarVal);
        }

        this.lastImage.set('avatar');
        this.imageFromCollection.set(true);
        if (avatarVal && avatarVal.indexOf('data:') === -1) {
          this.eventService.getImageAsBase64(this.eventService.getFileStream(avatarVal)).subscribe({
            next: (base64String) => {
              this.imgBase64.set(base64String);
            },
            error: (error) => {
              console.error('error Base64:', error);
            }
          });
        }
      }
    });

    effect(() => {
      if (this.page() === 'profile') {
        this.imageFromCollection.set(true);
      }
      if (this.page() === 'blog-new') {
        this.ratioX.set(16);
        this.ratioY.set(9);
      }
      if (this.defaultRatio() && this.hideRatioPages().includes(this.page())) {
        this.hideRatio.set(true);
      }
    });

    effect(() => {
      const fileVal = this.file();
      if (fileVal) {
        this.fileChangeEvent(fileVal);
      }
    });
  }

  fileChangeEvent(event: any): void {
    if (event.target.files[0].size < 10485760) {
      this.imgEvent.set(event);
      this.defaultImageFile.set(event);
      if (event.target.files[0].size > 1048576) {
        this.compressImage(event.target.files[0]);
      } else {
        this.loadImage(event.target.files[0]);
      }
    } else {
      let title = this.translate.instant('failure');
      let msg = this.translate.instant('too_big_image');
      this.toastr.error(msg, title);
    }
  }

  loadImage(file: any) {
    this.uploadedImg.set(file);
    this.defaultImage.set(file);
    this.fileName.set(file.name);
    if (this.checkFormatOfFile(file.name)) {
      if (!this.file()) {
        this.sendImg.emit(file);
      }
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.img.set(e.target.result);
        this.imageFromUser.set(e.target.result);
      };
      reader.readAsDataURL(file);
      this.imageFromCollection.set(false);
      this.showUndo.set(false);
      if (this.page() === 'profile') {
        this.uploadImg();
      }
      if (this.defaultRatio() && this.hideRatioPages().includes(this.page())) {
        this.cropImage();
      }
      this.firstImageLoad.set(true);
      this.tempImage.set('new');
    } else {
      this.toastr.error('Bad file format', 'Error!');
    }
  }

  compressImage(file: any) {
    let _quality = 0.8;
    if (file.size > 5000000) {
      _quality = 0.4;
    } else if (file.size > 2000000) {
      _quality = 0.6;
    } else if (file.size > 1000000) {
      _quality = 0.8;
    }

    new ImageCompressor(file, {
      quality: _quality,
      maxWidth: 800,
      success: (result: any) => {
        this.loadImage(result);
      },
      error: (e: any) => {
        console.log(e.message);
      },
    });
  }

  cropImage() {
    if (this.defaultRatio() && this.hideRatioPages().includes(this.page())) {
      this.useRatio.set(true);
      this.setRatio(16, 10);
    }
    this.openCropper.set(true);
    this.cropperOpened.emit(true);
    this.imageChangedEvent.set(this.defaultImageFile());
    this.showAvatarImage.set(false);
    this.showUndo.set(false);
    this.showCropMessage.set(true);
    setTimeout(() => {
      this.showCropMessage.set(false);
    }, 5000);
  }

  imageCroppedInFile(imageFile: any) {
    this.cropptedFile.set(imageFile);
  }

  imageLoaded() {
    // show cropper
  }

  loadImageFailed() {
    // show message
  }

  deleteImage() {
    const dialogRef = this.dialog.open(FuseDeleteDialogComponent, {
      panelClass: 'unsave-form-dialog',
      width: '300px',
      data: {title: 'Delete', message: 'deleteConfirm'}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.img.set('');
        this.sendImg.emit('');
        this.lastImage.set('');
        this.tempImage.set('');
      }
    });
  }

  choosePicture() {
    let dialogRef = this.dialog.open(PictureModalComponent, {
      panelClass: 'picture-form-dialog',
      width: '70vw',
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.img.set(res.src);
        this.collectionImage.set(res.src);
        this.imageFromCollection.set(true);
        this.showUndo.set(false);
        this.sendImg.emit({imgType: 'url', img: res.src});
        setTimeout(() => {
          if (res.src && res.src.indexOf('data:') === -1) {
            this.eventService.getImageAsBase64(this.eventService.getFileStream(res.src)).subscribe({
              next: (base64String) => {
                this.imgBase64.set(base64String);
                this.tempImage.set('collection');
                if (this.defaultRatio() && this.hideRatioPages().includes(this.page())) {
                  this.cropImage();
                }
              },
              error: (error) => {
                console.error('error Base64:', error);
              }
            });
          }
        }, 100);
      }
    });
  }

  checkFormatOfFile(name: string) {
    const exts = ['.png', '.gif', '.jpeg', '.jpg'];
    return (new RegExp('(' + exts.join('|').replace(/\./g, '\\.') + ')$')).test(name.toLowerCase());
  }

  saveAvatar() {
    this.lastImage.set(this.tempImage());
    if (!this.fileName() && this.img()) {
      this.fileName.set(this.img());
    }
    const _uploadedImg: any = this.cropptedFile();
    _uploadedImg.lastModifiedDate = new Date();
    _uploadedImg.name = this.fileName();
    this.uploadedImg.set(_uploadedImg);

    this.blobToFile(_uploadedImg.blob, this.fileName()).then((result: any) => {
      this.sendImg.emit(result);
    });

    this.imgEvent.set({
      target: {
        files: [_uploadedImg]
      }
    });

    if (this.page() === 'profile') {
      this.blobToFile(_uploadedImg.blob, this.fileName()).then(result => {
        this.uploadedImg.set(result);
        this.uploadImg();
      });
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.img.set(e.target.result);
      if (this.lastImage() === 'collection') {
        this.collectionImage.set(e.target.result);
      }
      if (this.lastImage() === 'new') {
        this.imageFromUser.set(e.target.result);
      }
    };
    reader.readAsDataURL(_uploadedImg.blob);

    this.showAvatarImage.set(true);
    this.openCropper.set(false);
    this.cropperOpened.emit(false);
    if (this.defaultRatio()) {
      this.showUndo.set(false);
    } else {
      this.showUndo.set(true);
    }
    this.useRatio.set(false);
    if (this.page() === 'blog-new') {
      this.ratioX.set(16);
      this.ratioY.set(9);
    } else {
      this.ratioX.set(1);
      this.ratioY.set(1);
    }
    this.firstImageLoad.set(false);
  }

  uploadImg() {
    this.userService.changeLogo(this.uploadedImg()).subscribe((response: any) => {
      localStorage.setItem('logo', response.logo);
      this.fuseConfig.setLogo(localStorage.getItem('logo'));
      this.img.set(response.logo);
      this.showAvatarImage.set(true);
    });
  }

  undoImage() {
    const defaultImg = this.defaultImage();
    this.uploadedImg.set(defaultImg);
    this.imgEvent.set({
      target: {
        files: [defaultImg]
      }
    });
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.img.set(e.target.result);
    };
    reader.readAsDataURL(defaultImg);
    this.showUndo.set(false);
    if (this.page() === 'profile') {
      this.uploadImg();
    }
  }

  cancelAvatar() {
    this.showAvatarImage.set(true);
    this.openCropper.set(false);
    this.cropperOpened.emit(false);
  }

  removeNewImg() {
    this.cancelAvatar();
    this.img.set('');
    setTimeout(() => {
      if (this.lastImage() === 'avatar' || this.lastImage() === 'collection') {
        const lastImg = this.lastImage() === 'avatar' ? this.avatar() : this.collectionImage();
        this.img.set(lastImg);
        this.imageChangedEvent.set('');
        this.imageFromCollection.set(true);
        if (lastImg && lastImg.indexOf('data:') === -1) {
          this.eventService.getImageAsBase64(this.eventService.getFileStream(lastImg)).subscribe({
            next: (base64String) => {
              this.imgBase64.set(base64String);
            },
            error: (error) => {
              console.error('error Base64:', error);
            }
          });
        }
      } else if (this.lastImage() === 'new') {
        this.img.set(this.imageFromUser());
      } else {
        this.imgBase64.set('');
        this.imgEvent.set('');
        this.tempImage.set('');
        this.sendImg.emit('');
      }
      this.firstImageLoad.set(false);
    });
  }

  setRatio(x: number, y: number) {
    this.ratioX.set(x);
    this.ratioY.set(y);
  }

  chooseRatio(e: boolean) {
    this.useRatio.set(e);
  }

  getImage(name: string) {
    if (!name) {
      return;
    }
    if (name && name.indexOf('data:') !== -1) {
      return name;
    }
    return this.eventService.getFileStream(name);
  }

  async blobToFile(theBlob: any, fileName: string) {
    return imageCompression(theBlob, {
      alwaysKeepResolution: true,
      maxSizeMB: 1,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    }).then(compressedFile => {
      return new File([compressedFile], fileName, {lastModified: new Date().getTime(), type: theBlob.type});
    });
  }
}
